import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClearRounded } from '@mui/icons-material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  Tooltip,
  Box,
} from '@mui/material';
import React, { useState } from 'react';

type Props = {
  value?: string; // e.g., "fa-solid fa-hand-dots"
  onChange: (iconClass: string | null) => void;
  isError?: boolean;
};

const FontAwesomePickerModal: React.FC<Props> = ({ value, onChange, isError }) => {
  const [open, setOpen] = useState(false);

  // Filter out only the icon definitions (starting with 'fa' and being objects)
  const icons = Object.keys(solidIcons).filter(
    (key) =>
      key.startsWith('fa') && typeof (solidIcons as any)[key] === 'object',
  ) as (keyof typeof solidIcons)[];

  const getStylePrefix = (prefix: string): string => {
    switch (prefix) {
      case 'fas':
        return 'fa-solid';
      case 'far':
        return 'fa-regular';
      case 'fab':
        return 'fa-brands';
      default:
        return 'fa-solid'; // default
    }
  };

  const getIconClass = (iconKey: keyof typeof solidIcons): string => {
    const iconDef = solidIcons[iconKey];
    if (!iconDef || typeof iconDef !== 'object') return '';
    const { prefix, iconName } = iconDef;
    const stylePrefix = getStylePrefix(prefix);
    return `${stylePrefix} fa-${iconName}`;
  };

  const getIconDefinitionByClass = (className: string) => {
    const iconName = className
      .split(' ')
      .find((cls) => cls.startsWith('fa-') && cls !== 'fa-solid')
      ?.replace('fa-', '');
    if (!iconName) return null;

    // Loop through all icons to find matching iconName
    return (
      icons
        .map((key) => solidIcons[key])
        .find((icon) => icon.iconName === iconName) || null
    );
  };

  return (
    <>
      {/* Selected Icon Preview */}
      {value ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'start',
            gap: '13px',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <FontAwesomeIcon icon={getIconDefinitionByClass(value)} size="2x" />

            <Tooltip title="Remove Icon" placement="top" arrow>
              <IconButton
                color="primary"
                sx={{
                  fontSize: '13px',
                  color: '#fff',
                  transition: 'all 0.35s ease',
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  padding: '2px',
                  backgroundColor: 'red',
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: '#c40808',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'red',
                    color: '#fff',
                    opacity: '0.3',
                  },
                }}
                onClick={() => onChange(null)}
              >
                <ClearRounded
                  sx={{
                    fontSize: 'inherit',
                    color: 'inherit',
                    transition: 'inherit',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          <Button
            size="small"
            variant="contained"
            onClick={() => setOpen(true)}
            startIcon={<AutorenewIcon sx={{ fontSize: '12px', mr: 0 }} />}
            sx={{ fontSize: '12px' }}
          >
            Change Icon
          </Button>
        </div>
      ) : (
        <Button
          size="small"
          variant="outlined"
          color={isError ? 'error' : 'primary'}
          sx={{ fontSize: '13px', marginBottom: '3px' }}
          onClick={() => setOpen(true)}
        >
          Choose Icon From Library
        </Button>
      )}

      {/* Icon Picker Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pick an Icon
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {icons.map((iconKey) => {
              const iconDef = solidIcons[iconKey];
              const className = getIconClass(iconKey);

              return (
                <Grid item xs={2} key={iconKey}>
                  <IconButton
                    onClick={() => {
                      onChange(className);
                      setOpen(false);
                    }}
                  >
                    <FontAwesomeIcon icon={iconDef} size="lg" />
                  </IconButton>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FontAwesomePickerModal;
